import React, { forwardRef, HTMLAttributes, ReactNode } from 'react';
import styled from 'styled-components';

interface StyledElementProps {
	color?: string;
	font?: string;
	fontFamily?: string;
	fontSize?: string;
	fontWeight?: string;
	lineHeight?: string;
	preserveLineBreaks?: boolean;
	textTransform?: 'uppercase' | 'lowercase' | 'capitalize';
}

const TextElement = styled.span<StyledElementProps>`
	${({ font }) => font && `font: ${font};`}
	${({ color }) => color && `color: ${color};`}
	${({ fontFamily }) => fontFamily && `font-family: ${fontFamily};`}
	${({ fontSize }) => fontSize && `font-size: ${fontSize};`}
	${({ fontWeight }) => fontWeight && `font-weight: ${fontWeight};`}
	${({ lineHeight }) => lineHeight && `line-height: ${lineHeight};`}
	${({ preserveLineBreaks }) => preserveLineBreaks && 'white-space: pre-line;'}
	${({ textTransform }) => textTransform && `text-transform: ${textTransform};`}

	&:focus {
		outline: 0;
	}
`;

interface Props extends StyledElementProps, HTMLAttributes<HTMLElement> {
	children: ReactNode;
	as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span';
}

type ForwardRefType = React.ForwardRefExoticComponent<Props & React.RefAttributes<HTMLElement>>;

export interface LegacyTextComponent extends ForwardRefType {
	h1: ForwardRefType;
	h2: ForwardRefType;
	h3: ForwardRefType;
	h4: ForwardRefType;
	h5: ForwardRefType;
	h6: ForwardRefType;
	p: ForwardRefType;
}

/**
 * # cortex:react-ui:Text
 * Component to render text-elements.
 */
export const LegacyText = forwardRef<HTMLElement, Props>(function LegacyText(
	{ children, as: asElement = 'span', color = 'inherit', ...rest },
	ref,
) {
	return (
		<TextElement as={asElement} color={color} ref={ref} {...rest}>
			{children}
		</TextElement>
	);
}) as LegacyTextComponent;

LegacyText.h1 = forwardRef<HTMLElement, Props>(function TextH1({ children, ...rest }, ref) {
	return (
		<LegacyText as='h1' ref={ref} {...rest}>
			{children}
		</LegacyText>
	);
});

LegacyText.h2 = forwardRef<HTMLElement, Props>(function TextH2({ children, ...rest }, ref) {
	return (
		<LegacyText as='h2' ref={ref} {...rest}>
			{children}
		</LegacyText>
	);
});

LegacyText.h3 = forwardRef<HTMLElement, Props>(function TextH3({ children, ...rest }, ref) {
	return (
		<LegacyText as='h3' ref={ref} {...rest}>
			{children}
		</LegacyText>
	);
});

LegacyText.h4 = forwardRef<HTMLElement, Props>(function TextH4({ children, ...rest }, ref) {
	return (
		<LegacyText as='h4' ref={ref} {...rest}>
			{children}
		</LegacyText>
	);
});

LegacyText.h5 = forwardRef<HTMLElement, Props>(function TextH5({ children, ...rest }, ref) {
	return (
		<LegacyText as='h5' ref={ref} {...rest}>
			{children}
		</LegacyText>
	);
});

LegacyText.h6 = forwardRef<HTMLElement, Props>(function TextH6({ children, ...rest }, ref) {
	return (
		<LegacyText as='h6' ref={ref} {...rest}>
			{children}
		</LegacyText>
	);
});

LegacyText.p = forwardRef<HTMLElement, Props>(function TextP({ children, ...rest }, ref) {
	return (
		<LegacyText as='p' ref={ref} {...rest}>
			{children}
		</LegacyText>
	);
});
